'use client';

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from 'ui/components/Carousel/Carousel';
import type { Language } from '@whoop/i18n';
import { useEffect, useState } from 'react';
import type { ShopAccessoryCategory } from 'services/generated/commerce-service/models/ShopAccessoryCategory';
import type { IPLPPage } from '../../lib/utils/productFetcher/getPlpContentHelpers';
import ProductCard from '@/components/ProductCard/ProductCard';
import {
  getPlpContentBySlug,
  getProductInformation,
} from '@/lib/utils/productFetcher/getPlpContent';
import {
  getAllProductsFromCategory,
  getFirstProductWithVariantInStock,
  getFirstVariantInStock,
} from 'ui/utils/productHelpers';

export interface PLPCarouselProps {
  country: string;
  language: Language;
  slug: string;
}

function PLPCarousel({
  country,
  language,
  slug,
}: PLPCarouselProps): JSX.Element {
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const [contentfulData, setContentfulData] = useState<IPLPPage | null>(null);
  const [accessoryGroups, setAccessoryGroups] = useState<
    ShopAccessoryCategory[]
  >([]);

  useEffect(() => {
    async function fetchData() {
      if (!contentfulData) {
        const data = await getPlpContentBySlug({ slug });
        setContentfulData(data);
      } else {
        const orderedAccessoryGroups = await getProductInformation({
          country,
          language,
          plpPage: contentfulData,
        });
        setAccessoryGroups(orderedAccessoryGroups);
      }
    }

    fetchData();
  }, [contentfulData]);

  useEffect(() => {
    if (!api) {
      return;
    }
    const updateState = () => {
      setCanScrollNext(api.canScrollNext());
      setCanScrollPrev(api.canScrollPrev());
    };
    updateState();

    api.on('scroll', () => {
      updateState();
    });
    api.on('resize', () => {
      updateState();
    });
  }, [api]);

  if (!contentfulData || accessoryGroups.length === 0) {
    return <></>;
  }

  return (
    <section className='w-full py-16'>
      <div className='mx-auto px-6 md:px-0'>
        <Carousel
          opts={{
            align: 'start',
          }}
          setApi={setApi}
        >
          <div className='flex justify-between'>
            <h2 className='pb-4 text-[length:--h2-size] font-bold md:pb-6 md:text-[length:--h2-size-md] lg:pb-14'>
              {contentfulData?.plpName}
            </h2>
            <div className='flex w-28 flex-wrap content-center gap-3'>
              <CarouselPrevious
                className={`rounded-full bg-black hover:bg-black ${
                  canScrollPrev ? '' : 'invisible'
                }`}
                fill='white'
                iconSize={15}
              />
              <CarouselNext
                className={`rounded-full bg-black hover:bg-black ${
                  canScrollNext ? '' : 'invisible'
                }`}
                fill='white'
                iconSize={15}
              />
            </div>
          </div>
          <CarouselContent>
            {accessoryGroups.map((category) => {
              const products = getAllProductsFromCategory(category);
              if (category.slug === null || products.length === 0) return null;
              return (
                <CarouselItem
                  className='flex basis-[100%] justify-center sm:basis-[50%] md:basis-[33.3%] lg:basis-[25%] xl:basis-[20%]'
                  key={products[0].product_id}
                >
                  <ProductCard
                    country={country}
                    currency='usd'
                    language={language}
                    productCategory={category}
                    products={products}
                    slug={category.slug}
                    // TODO - STFT-533 - Handle pack pricing. Deferring for now to work on more pressing features
                    // For now, don't add pack products to this carousel or else the price will be $0
                    initialVariant={getFirstVariantInStock(
                      getFirstProductWithVariantInStock(products),
                    )}
                    addPlus={false}
                  />
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
}

export default PLPCarousel;
